import useMediaQuery from "@mui/material/useMediaQuery";
import { useIntersectionObserver, useIsClient } from "@uidotdev/usehooks";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import SingleJobMediaModal from "../../jobs/SingleJobWrapper/SingleJobMediaModal/SingleJobMediaModal";
import { SectionContainer } from "../styles";
import {
  GridStyled,
  ImageStyled,
  OutlinedButton,
  ShowAllMediaButton,
  ShowAllMediaIcon,
  SkeletonContainer,
} from "./styles";

export default function CompanyPictures(props) {
  const belowLg = useMediaQuery("(max-width: 1279px)");
  const belowTablet = useMediaQuery("(max-width: 640px)");
  const { t } = useTranslation();

  const { companyDetail, setCurrentTab } = props || {};
  const { images } = companyDetail || {};

  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
    rootMargin: "-200px 0px -60% 0px",
  });
  
  useEffect(() => {
    if (entry?.isIntersecting) {
      setCurrentTab(1);
    }
  }, [entry?.isIntersecting, setCurrentTab]);

  // START - Processing and formatting media data structure

  const formattedImageUrls =
    Array.isArray(images) && images.length > 0
      ? images.map((companyImg) => (companyImg.image ? companyImg.image : ""))
      : [];

  // END - Processing and formatting media data structure

  function conditionalRenderMedia() {
    const removeFirstItem = Array.isArray(formattedImageUrls)
      ? formattedImageUrls.slice(1)
      : [];

    // index 0 and 2 should stretch
    // if length of removeFirstItem is odd
    const stretchOddIndex = removeFirstItem.length % 2 !== 0 ? true : false;

    return removeFirstItem.map((mediaUrl, index) => {
      if (typeof mediaUrl === "string" && mediaUrl) {
        return (
          <GridStyled
            key={index}
            height={"100%"}
            sx={{
              // if its the last item in the list, and it is an odd number, stretch it
              overflow: "hidden",
              borderRadius: "10px",
              gridColumn:
                stretchOddIndex && index === removeFirstItem.length - 1
                  ? "span 2"
                  : null,
            }}
          >
            <ImageStyled
              draggable={false}
              src={mediaUrl}
              onClick={() => handleClickMedia(index + 1)}
              alt={t("companydetail.company_media_alt", {
                company: companyDetail?.name,
                index: index + 1,
              })}
              aria-label={t("companydetail.company_media_aria", {
                company: companyDetail?.name,
                index: index + 1,
              })}
              loading="eager"
            />
          </GridStyled>
        );
      } else {
        return null;
      }
    });
  }

  // Responsible for showing the correct indexed media on the modal popup
  function handleClickMedia(mediaIndex) {
    sendTrackingEvent({
      event: "CE_click-media-image-cdp",
      "company-id": companyDetail?.id,
    });

    setOpenModal(true);
    setCurrentMediaIndex(mediaIndex);
  }

  // force CSR with a state

  const isClient = useIsClient();
  const [currentMediaIndex, setCurrentMediaIndex] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  function handleOpenModal() {
    sendTrackingEvent({
      event: "CE_click-show-all-media-cdp",
      "company-id": companyDetail?.id,
    });

    setCurrentMediaIndex(0);
    setOpenModal(true);
  }

  return isClient && formattedImageUrls.length > 0 ? (
    <SectionContainer
      aria-label={t("companydetail.media_section_aria")}
      id="CompanyOffice"
      ref={ref}
      sx={{ padding: "0 2rem" }}
    >
      <GridStyled
        container
        position={"relative"}
        flexWrap={"nowrap"}
        columns={12}
        marginBottom={belowTablet && "1rem"}
        sx={{
          overflowX: belowTablet ? "scroll" : null,
          overflowY: "clip",
        }}
        columnGap={"10px"}
        maxHeight={belowLg ? "220px" : belowTablet ? "300px" : "370px"}
      >
        <GridStyled item tablet={formattedImageUrls.length > 1 ? 6 : 12}>
          <ImageStyled
            width={"615px"}
            height={"370px"}
            src={formattedImageUrls[0]}
            draggable={false}
            onClick={() => handleClickMedia(0)}
            alt={t("companydetail.company_media_alt", {
              company: companyDetail?.name,
              index: 1,
            })}
            aria-label={t("companydetail.company_media_aria", {
              company: companyDetail?.name,
              index: 1,
            })}
            loading="eager"
          />
        </GridStyled>
        {formattedImageUrls.length > 1 ? (
          <GridStyled
            item
            tablet={6}
            display={belowTablet ? "flex" : "grid"}
            gridTemplateColumns={"repeat(2, 1fr)"}
            gridAutoRows={
              belowLg
                ? formattedImageUrls.length < 4
                  ? "auto"
                  : "105px"
                : formattedImageUrls.length < 4
                ? "auto"
                : "49%"
            }
            gap={"10px"}
          >
            {conditionalRenderMedia()}
          </GridStyled>
        ) : null}
        {!belowTablet ? (
          <ShowAllMediaButton onClick={handleOpenModal} id="show-all-media">
            <ShowAllMediaIcon />
            {t("companydetail.show_all_media")}
          </ShowAllMediaButton>
        ) : null}
      </GridStyled>
      {belowTablet ? (
        <OutlinedButton
          variant="outlined"
          onClick={handleOpenModal}
          id="show-all-media"
        >
          <ShowAllMediaIcon /> {t("companydetail.show_all_media")}
        </OutlinedButton>
      ) : null}
      <SingleJobMediaModal
        companyDetail={companyDetail}
        media={formattedImageUrls} // all the media of the company
        currentMediaIndex={currentMediaIndex} // index of the selected media for viewing upon onclick event
        open={openModal} // boolean to toggle whether modal is open or close
        setOpenModal={setOpenModal} // setState to open or close the modal
        handleClickMedia={handleClickMedia} // function to open the modal, and change state of current media index
      />
    </SectionContainer>
  ) : (
    <SkeletonContainer />
  );
}
